import React, { useCallback, useEffect, useState } from 'react';
import { useApiAuth } from '../../hooks/useApiAuth';
import { PORTFOLIO_CATEGORIES } from '../../pages/Portfolio';

export default function PortfolioManager({ setStatus }) {
  const { authFetch } = useApiAuth();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    category: PORTFOLIO_CATEGORIES[0].key,
    title: '',
    description: '',
    file: null,
  });
  const [openCategory, setOpenCategory] = useState(PORTFOLIO_CATEGORIES[0].key);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ title: '', description: '' });

  const load = useCallback(async () => {
    try {
      const p = await authFetch('/api/admin/portfolio');
      setItems(p.items || []);
    } catch (e) {
      setStatus(`Load error: ${e.message}`);
    }
  }, [authFetch, setStatus]);

  useEffect(() => { load(); }, [load]);

  const addPhoto = async (e) => {
    e.preventDefault();
    if (!form.file) { setStatus('Pick an image first.'); return; }
    setBusy(true); setStatus('Uploading…');
    try {
      const up = await authFetch(
        `/api/admin/upload?filename=${encodeURIComponent(form.file.name)}`,
        { method: 'POST', body: form.file }
      );
      const nextSort = (items.filter((i) => i.category === form.category).length + 1) * 10;
      await authFetch('/api/admin/portfolio', {
        method: 'POST',
        body: {
          category: form.category,
          title: form.title,
          description: form.description,
          image_url: up.url,
          sort_order: nextSort,
        },
      });
      setForm({ ...form, title: '', description: '', file: null });
      setStatus('Photo added.');
      await load();
    } catch (e) { setStatus(`Add error: ${e.message}`); }
    setBusy(false);
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this photo?')) return;
    setBusy(true);
    try {
      await authFetch(`/api/admin/portfolio?id=${id}`, { method: 'DELETE' });
      setStatus('Deleted.');
      await load();
    } catch (e) { setStatus(`Delete error: ${e.message}`); }
    setBusy(false);
  };

  const startEdit = (it) => {
    setEditing(it.id);
    setEditForm({ title: it.title || '', description: it.description || '' });
  };
  const cancelEdit = () => { setEditing(null); setEditForm({ title: '', description: '' }); };
  const saveEdit = async () => {
    setBusy(true);
    try {
      await authFetch(`/api/admin/portfolio?id=${editing}`, { method: 'PUT', body: editForm });
      setStatus('Updated.');
      cancelEdit();
      await load();
    } catch (e) { setStatus(`Update error: ${e.message}`); }
    setBusy(false);
  };

  const move = async (categoryKey, id, direction) => {
    const catItems = items
      .filter((i) => i.category === categoryKey)
      .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.id - b.id);
    const idx = catItems.findIndex((i) => i.id === id);
    const swapIdx = idx + direction;
    if (swapIdx < 0 || swapIdx >= catItems.length) return;
    const a = catItems[idx], b = catItems[swapIdx];
    setBusy(true);
    try {
      await authFetch('/api/admin/portfolio', {
        method: 'PATCH',
        body: { items: [
          { id: a.id, sort_order: b.sort_order ?? swapIdx * 10 },
          { id: b.id, sort_order: a.sort_order ?? idx * 10 },
        ] },
      });
      await load();
    } catch (e) { setStatus(`Reorder error: ${e.message}`); }
    setBusy(false);
  };

  return (
    <div>
      <section className="admin-section">
        <h2>Add a portfolio photo</h2>
        <form onSubmit={addPhoto} className="admin-form">
          <label>Category
            <select
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
            >
              {PORTFOLIO_CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.title}</option>)}
            </select>
          </label>
          <label>Title (optional)
            <input type="text" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          </label>
          <label>Description (optional)
            <input type="text" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </label>
          <label>Image file
            <input type="file" accept="image/*" onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })} />
          </label>
          <button type="submit" disabled={busy} className="admin-btn admin-btn-primary">Add photo</button>
        </form>
      </section>

      <section className="admin-section">
        <h2>Photos, sorted by category</h2>
        <div className="admin-cat-tabs">
          {PORTFOLIO_CATEGORIES.map((c) => (
            <button
              key={c.key}
              className={`admin-tab ${openCategory === c.key ? 'is-active' : ''}`}
              onClick={() => setOpenCategory(c.key)}
            >{c.title}</button>
          ))}
        </div>
        {PORTFOLIO_CATEGORIES.filter((c) => c.key === openCategory).map((cat) => {
          const dbItems = items
            .filter((i) => i.category === cat.key)
            .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.id - b.id);
          return (
            <div key={cat.key}>
              <h3 className="admin-subhead">Photos ({dbItems.length})</h3>
              {dbItems.length === 0 && (
                <p className="admin-muted">
                  No photos in this category yet. If you haven't hit <strong>Initialize DB</strong>{' '}
                  since the last update, click it once — that imports the site's built-in photos
                  here so you can reorder or replace them.
                </p>
              )}
              <div className="admin-grid">
                {dbItems.map((it, idx) => (
                  <div key={it.id} className="admin-card">
                    <img src={it.image_url} alt={it.title || cat.title} />
                    <div>
                      {editing === it.id ? (
                        <div style={{ display: 'grid', gap: 6 }}>
                          <input
                            placeholder="Title"
                            value={editForm.title}
                            onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                          />
                          <input
                            placeholder="Description"
                            value={editForm.description}
                            onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                          />
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button className="admin-btn admin-btn-primary" onClick={saveEdit} disabled={busy}>Save</button>
                            <button className="admin-btn" onClick={cancelEdit} disabled={busy}>Cancel</button>
                          </div>
                        </div>
                      ) : (
                        <>
                          {it.title && <strong>{it.title}</strong>}
                          {it.description && <div className="admin-muted">{it.description}</div>}
                          {!it.title && !it.description && <div className="admin-muted">Untitled</div>}
                        </>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <button className="admin-btn" disabled={busy || idx === 0} onClick={() => move(cat.key, it.id, -1)}>↑</button>
                      <button className="admin-btn" disabled={busy || idx === dbItems.length - 1} onClick={() => move(cat.key, it.id, 1)}>↓</button>
                      {editing !== it.id && <button className="admin-btn" disabled={busy} onClick={() => startEdit(it)}>Edit</button>}
                      <button className="admin-btn admin-btn-danger" disabled={busy} onClick={() => remove(it.id)}>Delete</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </section>
    </div>
  );
}
